import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  User as FirebaseUser,
  GoogleAuthProvider,
  signInWithRedirect,
  signInWithPopup,
  getRedirectResult,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
  signInWithCredential
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

interface AuthContextType {
  isAuthenticated: boolean;
  user: FirebaseUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Firestore 사용자 문서 생성 함수 (앱에서 참고)
const createUserDocument = async (user: FirebaseUser) => {
  try {
    console.log('🔄 Starting createUserDocument for user:', user.uid);
    console.log('📧 User email:', user.email);
    console.log('🔥 Firestore instance:', !!db);
    
    if (!db) {
      console.error('❌ Firestore instance is null or undefined');
      throw new Error('Firestore not initialized');
    }
    
    const userRef = doc(db, 'users', user.uid);
    console.log('📄 Firestore reference created:', userRef.path);
    
    // 문서가 이미 존재하는지 확인
    console.log('🔍 Checking if document exists...');
    const userSnap = await getDoc(userRef);
    console.log('📋 Document exists check result:', userSnap.exists());

    if (!userSnap.exists()) {
      console.log('🆕 Creating new user document...');
      
      const userData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || null,
        photoURL: user.photoURL || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        plan: 'free_trial',
        lastLoginAt: serverTimestamp(),
        isActive: true,
        createdBy: 'web_app',
        version: '1.0.0'
      };

      console.log('📝 User data to save:', {
        ...userData,
        createdAt: 'serverTimestamp()',
        updatedAt: 'serverTimestamp()',
        lastLoginAt: 'serverTimestamp()'
      });
      
      await setDoc(userRef, userData);
      console.log('✅ Firestore user document created successfully:', user.uid);
      
      // 문서 생성 확인
      const verifySnap = await getDoc(userRef);
      if (verifySnap.exists()) {
        console.log('✅ Document creation verified:', verifySnap.data());
        return true;
      } else {
        console.error('❌ Document was not created despite successful setDoc');
        return false;
      }
      
    } else {
      console.log('✅ Firestore user document already exists:', user.uid);
      console.log('📄 Existing document data:', userSnap.data());
      
      // 기존 사용자의 마지막 로그인 시간 업데이트
      try {
        await setDoc(userRef, {
          lastLoginAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }, { merge: true });
        console.log('✅ Updated last login time for existing user');
      } catch (updateError) {
        console.warn('⚠️ Failed to update last login time:', updateError);
      }
      
      return true;
    }
  } catch (firestoreError: any) {
    console.error('❌ Error in createUserDocument:', firestoreError);
    console.error('❌ Error code:', firestoreError.code);
    console.error('❌ Error message:', firestoreError.message);
    
    // 수동 타임스탬프로 재시도
    try {
      console.log('🔄 Retrying with manual timestamp...');
      const userRef = doc(db, 'users', user.uid);
      const manualTimestamp = new Date();
      
      // 14일 후 만료되는 무료 체험 설정
      const trialEndDate = new Date();
      trialEndDate.setDate(trialEndDate.getDate() + 14);
      
      const userData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || null,
        photoURL: user.photoURL || null,
        createdAt: manualTimestamp,
        updatedAt: manualTimestamp,
        plan: 'free_trial',
        trialEndsAt: trialEndDate, // 14일 무료 체험 만료일 추가
        lastLoginAt: manualTimestamp,
        isActive: true,
        createdBy: 'web_app_retry',
        version: '1.0.0'
      };

      console.log('📝 Retry user data:', userData);
      await setDoc(userRef, userData);
      
      const retryVerifySnap = await getDoc(userRef);
      if (retryVerifySnap.exists()) {
        console.log('✅ Firestore user document created on retry:', user.uid);
        return true;
      } else {
        console.error('❌ Retry also failed - document not created');
        return false;
      }
      
    } catch (retryError) {
      console.error('❌ Failed to create Firestore user document on retry:', retryError);
      return false;
    }
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeoutError, setTimeoutError] = useState(false);

  // Firebase Auth 상태 변경 감지 및 리다이렉트 결과 처리
  useEffect(() => {
    let isMounted = true;

    // 5초 타임아웃 설정
    const timeoutId = setTimeout(() => {
      if (isMounted && loading) {
        console.warn('⚠️ Auth initialization timeout - proceeding anyway');
        setTimeoutError(true);
        setLoading(false);
      }
    }, 5000);

    const initializeAuth = async () => {
      try {
        console.log('🔄 Initializing Firebase Auth persistence...');
        await setPersistence(auth, browserLocalPersistence);
        console.log('✅ Firebase Auth persistence set to local');
      } catch (error) {
        console.warn('⚠️ Failed to set local persistence, trying session:', error);
        try {
          await setPersistence(auth, browserSessionPersistence);
          console.log('✅ Firebase Auth persistence set to session');
        } catch (sessionError) {
          console.warn('⚠️ Failed to set session persistence, using memory:', sessionError);
          await setPersistence(auth, inMemoryPersistence);
          console.log('✅ Firebase Auth persistence set to memory');
        }
      }

      // 리다이렉트 결과 처리
      try {
        console.log('🔄 Checking for redirect result...');
        const result = await getRedirectResult(auth);
        if (result?.user && isMounted) {
          console.log('✅ Google sign-in redirect result received:', result.user.uid);
          setUser(result.user);
          setIsAuthenticated(true);
          
          // Firestore 사용자 문서 생성
          try {
            const documentCreated = await createUserDocument(result.user);
            if (!documentCreated) {
              console.warn('⚠️ Failed to create Firestore document, but proceeding.');
            }
          } catch (error) {
            console.error('❌ Error creating user document:', error);
          }
          
                // 로그인 성공 후 대시보드로 리다이렉트 (scanvcf.com으로 강제 이동)
      console.log('🔄 Redirecting to dashboard after successful login...');
      window.location.replace('https://scanvcf.com/dashboard');
        } else {
          console.log('ℹ️ No redirect result found');
        }
      } catch (error) {
        console.error('❌ Redirect result error:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Auth 상태 변경 감지
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (isMounted) {
        console.log('🔄 Auth state changed:', user ? `User ${user.uid}` : 'No user');
      setUser(user);
      setIsAuthenticated(!!user);
      setLoading(false);
      }
    });

    // 초기화 실행
    initializeAuth();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      const result = await signInWithEmailAndPassword(auth, email, password);
      setUser(result.user);
      setIsAuthenticated(true);
      
      // Firestore 사용자 문서 생성
      try {
        const documentCreated = await createUserDocument(result.user);
        if (!documentCreated) {
          console.warn('⚠️ Failed to create Firestore document, but proceeding.');
        }
      } catch (error) {
        console.error('❌ Error creating user document:', error);
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // 앱의 구글 로그인 로직을 참고한 웹 구현
  const loginWithGoogle = async () => {
    console.log('🔄 Starting Google sign-in...');
    console.log('📊 Current configuration info:');
    console.log('  - Domain:', window.location.hostname);
    console.log('  - Firebase Project: scanvcf-375fb');
    console.log('  - App ID: 1:603606213699:web:131f4e659f55a7b0f020f6');

    if (loading) {
      console.log('⚠️ Login already in progress, ignoring request');
      return;
    }

    setLoading(true);

    try {
      // 기존 Firebase Auth 상태 확인
      console.log('🔄 Checking existing Firebase auth state...');
      const currentUser = auth.currentUser;
      
      if (currentUser) {
        console.log('✅ User already authenticated with Firebase:', currentUser.uid);
        console.log('🔄 Proceeding with Google sign-in for account linking...');
      } else {
        console.log('🔄 No existing Firebase session, proceeding with fresh Google sign-in...');
      }

      const provider = new GoogleAuthProvider();
      
      // Google OAuth 설정 개선 - 모든 Google 계정 허용
      provider.setCustomParameters({ 
        prompt: 'select_account',
        access_type: 'offline'
        // hd 파라미터 제거 - 모든 Google 계정 허용
      });

      console.log('🔄 Using popup method for better user experience...');
      console.log('🔄 Provider configured:', provider);
      
      // 팝업 방식으로 변경하여 사용자가 Firebase 내부 주소를 보지 않도록 함
      const result = await signInWithPopup(auth, provider);
      console.log('✅ Google sign-in successful:', result.user.uid);
      
      // Firestore 사용자 문서 생성
      try {
        const documentCreated = await createUserDocument(result.user);
        if (!documentCreated) {
          console.warn('⚠️ Failed to create Firestore document, but proceeding.');
        }
      } catch (error) {
        console.error('❌ Error creating user document:', error);
      }
      
      // 로그인 성공 후 대시보드로 리다이렉트 (scanvcf.com으로 강제 이동)
      window.location.href = 'https://scanvcf.com/dashboard';
    } catch (error: any) {
      console.error('❌ Google sign-in error:', error);
      
      // 팝업이 차단된 경우 리다이렉트 방식으로 폴백
      if (error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-user') {
        console.log('🔄 Popup blocked, trying redirect method...');
        try {
          const redirectProvider = new GoogleAuthProvider();
          redirectProvider.setCustomParameters({ 
            prompt: 'select_account',
            access_type: 'offline'
            // hd 파라미터 제거 - 모든 Google 계정 허용
          });
          await signInWithRedirect(auth, redirectProvider);
          // 리다이렉트 후 결과는 handleRedirectResult에서 처리됨
        } catch (redirectError) {
          console.error('❌ Redirect method also failed:', redirectError);
          throw new Error('Google 로그인에 실패했습니다. 팝업을 허용하거나 다시 시도해주세요.');
        }
      } else {
        throw new Error(error.message || 'Google 로그인에 실패했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await signOut(auth);
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (email: string, password: string, name: string) => {
    try {
      setLoading(true);
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // 사용자 프로필 업데이트 (이름 설정)
      // await updateProfile(result.user, { displayName: name });
      
      setUser(result.user);
      setIsAuthenticated(true);
      
      // Firestore 사용자 문서 생성
      try {
        const documentCreated = await createUserDocument(result.user);
        if (!documentCreated) {
          console.warn('⚠️ Failed to create Firestore document, but proceeding.');
        }
      } catch (error) {
        console.error('❌ Error creating user document:', error);
      }
    } catch (error) {
      console.error('Register error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    loginWithGoogle,
    logout,
    register,
  };

  // 로딩 중일 때 스켈레톤 UI 표시
  if (loading) {
    return (
      <AuthContext.Provider value={value}>
        <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">
              {timeoutError ? 'Loading timeout - please refresh' : 'Loading...'}
            </p>
            {timeoutError && (
              <button 
                onClick={() => window.location.reload()} 
                className="mt-4 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
              >
                Refresh Page
              </button>
            )}
          </div>
        </div>
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 